import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CollapseProps, StepProps, TimelineItemProps } from "antd";
import { Collapse, Steps, Timeline, message } from "antd";
import { createFromIconfontCN } from "@ant-design/icons";
import _ from "lodash";
import AMapLoader from '@amap/amap-jsapi-loader';
import sMarkerPng from "@/images/service/s_smarker.png";
import eMarkerPng from "@/images/service/e_smarker.png";
import arrivePng from "@/images/service/arrive.png";
import { serviceApi } from "@/apis";
import classNames from "classnames";
import moment from "moment";
import Inspection from "./inspection";

function formatDate(d: any) {
  if (!d) return;
  return moment(parseFloat(d)).format("YYYY-MM-DD HH:mm:ss");
}

const MyIcon = createFromIconfontCN({
  scriptUrl: "//at.alicdn.com/t/c/font_4167130_nsifub4p4v.js", // 在 iconfont.cn 上生成
});

const stepItemsInit: StepProps[] = [
  {
    title: "待发货",
    status: "finish",
    icon: <MyIcon className="collapse-icon" type="icon-daifahuo1" />,
  },
  {
    title: "已发货",
    status: "finish",
    icon: <MyIcon className="collapse-icon" type="icon-yifahuo1" />,
  },
  {
    title: "配送中",
    status: "finish",
    icon: <MyIcon className="collapse-icon" type="icon-peisongzhong1" />,
  },
  {
    title: "已签收",
    status: "finish",
    icon: <MyIcon className="collapse-icon" type="icon-yiqianshou1" />,
  },
];

const stateMap: any = {
  0: {
    label: "待发货",
    color: "#5d38c0",
  },
  1: {
    label: "已发货",
    color: "#00ba9c",
  },
  2: {
    label: "配送中",
    color: "#fa9b25",
  },
  3: {
    label: "已签收",
    color: "#086208",
  },
};

const Collapsetitle = ({
  info,
  delivery,
  openInspection,
}: {
  info?: DeliveryDetailItems;
  delivery?: DeliveryOrderInfoItem;
  openInspection?: Function;
}) => {
  const [stateInfo, setStateInfo] = useState<any>();
  const [stepItems, setStepItems] = useState<StepProps[]>([]);
  const [hasInspection, setHasInspection] = useState(false);
  useEffect(() => {
    if (delivery) {
      const _delivery = _.cloneDeep(delivery);
      // 流程信息
      const statusInfoList = _delivery.statusInfoList.reverse();
      // 最新物流信息取倒叙的第一个
      // status: 已签收，已到达，配送中，已派车，准备中
      // newState： 已签收 3，配送中 2，已发货 1，待发货 0
      const _status = statusInfoList[0] && statusInfoList[0].status;
      const newState =
        _status === "已签收"
          ? 3
          : _status === "已派车"
          ? 1
          : _status === "准备中"
          ? 0
          : 2;
      const _stateInfo = stateMap[newState];
      setStateInfo(_stateInfo);
      const _stepItemsInit = _.cloneDeep(stepItemsInit);
      const _stepItems = _stepItemsInit.map((el: StepProps, i: number) => {
        if (i === newState) {
          el.status = "process";
        }
        if (i > newState) {
          el.status = "wait";
        }
        return el;
      });
      setStepItems(_stepItems);
    }
  }, [delivery]);

  useEffect(() => {
    const orderNo = info?.orderNo;
    if (orderNo) {
      try {
        const getItem = (orderno: string, ztype: string) => {
          serviceApi
            .getInspectionInfo({
              orderno,
              ztype,
            })
            .then((res) => {
              if (res && res.result && res.result.prueflos) {
                setHasInspection(true);
              }
            })
            .catch((err: any) => {
              console.log("err", err);
            });
        };
        getItem(orderNo, "A");
        getItem(orderNo, "C");
      } catch (error) {
        console.log(error);
      }
    }
  }, [info?.orderNo]);

  return (
    <div className="collapse-title">
      <div className="title-orderNo">
        <p className="order">
          发货单号:<span>{info?.orderNo}</span>
        </p>
        <p className="state">
          {stateInfo ? (
            <span style={{ color: stateInfo.color }}>{stateInfo.label}</span>
          ) : null}
        </p>
        <p
          className={`inspe ${hasInspection ? '' : 'disabled'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (hasInspection) {
              openInspection && openInspection(info?.orderNo, info?.items);
            }
          }}
        >
          查看质检单
        </p>
      </div>
      <Steps className="collapse-step" items={stepItems} />
    </div>
  );
};

const CollapseBody = ({
  address,
  delivery,
  info,
  isActive,
}: {
  address: string;
  delivery?: DeliveryOrderInfoItem;
  info?: any;
  isActive?: boolean;
}) => {
  const mapContainer = useRef<any>();
  const mapInstance = useRef<any>();
  const markersRef = useRef<any[]>([]);
  const polylineRef = useRef<any>();
  const infoWindowRef = useRef<any>();
  const isInitialized = useRef(false);

  const [timelineItems, setTimelineItems] = useState<TimelineItemProps[]>([]);
  const [mapReady, setMapReady] = useState(false);

  // 初始化高德地图 - 只在面板激活且未初始化时执行
  useEffect(() => {
    if (!isActive || !mapContainer.current || isInitialized.current) return;

    // 检查全局 AMap 是否已加载
    if ((window as any).AMap) {
      // 如果已加载，直接使用
      const AMap = (window as any).AMap;
      const map = new AMap.Map(mapContainer.current, {
        zoom: 9,
        center: [116.404, 39.915],
        viewMode: '2D',
      });

      // 添加控件
      map.addControl(new AMap.Scale());
      map.addControl(new AMap.ToolBar());

      mapInstance.current = map;
      isInitialized.current = true;
      setMapReady(true);
    } else {
      // 如果未加载，则加载
      AMapLoader.load({
        key: 'b0fabf08f16429ca7117e67a7c87b0a8', // 开发者 Key
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.InfoWindow'],
      })
        .then((AMap) => {
          if (!mapContainer.current) return;
          
          const map = new AMap.Map(mapContainer.current, {
            zoom: 9,
            center: [116.404, 39.915],
            viewMode: '2D',
          });

          // 添加控件
          map.addControl(new AMap.Scale());
          map.addControl(new AMap.ToolBar());

          mapInstance.current = map;
          (window as any).AMap = AMap;
          isInitialized.current = true;
          setMapReady(true);
        })
        .catch((e) => {
          console.error('高德地图加载失败', e);
          message.error('地图加载失败');
        });
    }

    return () => {
      // 清理覆盖物
      markersRef.current.forEach((marker) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      markersRef.current = [];
      
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      
      // 销毁地图实例
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
        isInitialized.current = false; // 重置初始化标记
        setMapReady(false); // 重置地图就绪状态
      }
    };
  }, [isActive]);

  // 当面板激活状态变化时，重新调整地图尺寸
  useEffect(() => {
    if (isActive && mapInstance.current && mapReady) {
      // 使用 setTimeout 确保 DOM 已经完全渲染
      setTimeout(() => {
        if (mapInstance.current) {
          // 调用 resize 方法重新计算地图容器尺寸
          mapInstance.current.resize();
          
          // 如果有标记点，重新适应视野
          if (markersRef.current.length > 0) {
            mapInstance.current.setFitView(markersRef.current, false, [80, 80, 80, 80]);
          }
        }
      }, 100);
    }
  }, [isActive, mapReady]);

  // 更新地图内容
  useEffect(() => {
    if (!delivery || !mapReady || !mapInstance.current || !isActive) return;

    const AMap = (window as any).AMap;
    const map = mapInstance.current;

    // 清除之前的标记和覆盖物
    markersRef.current.forEach((marker) => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }

    const _delivery: DeliveryOrderInfoItem = _.cloneDeep(delivery);
    // 流程信息
    const statusInfoList = _delivery.statusInfoList.reverse();
    const _status = statusInfoList[0] && statusInfoList[0].status;
    const newState =
      _status === "已签收"
        ? 3
        : _status === "已派车"
        ? 1
        : _status === "准备中"
        ? 0
        : 2;
    const _stateInfo = stateMap[newState];

    const _baseInfo = _delivery.baseInfo;
    const _timelineItems = statusInfoList.map(
      (el: DeliveryOrderInfoStatusItem, i: number) => {
        return {
          color: i === 0 ? "gray" : "#004098",
          children: (
            <>
              <p className={classNames(["title", { active: i === 0 }])}>
                {el.status}
              </p>
              {el.status === "已签收" ? (
                <p className="info active">签收地址：{address}</p>
              ) : null}
              {el.status === "已签收" || el.status === "准备中" ? null : (
                <p className="info">
                  配送车辆：{_baseInfo.vehicleNumber}&nbsp;&nbsp;&nbsp;
                </p>
              )}
              <p className="timestamp">{el.operateTime}</p>
            </>
          ),
        };
      }
    );
    setTimelineItems(_timelineItems);

    // 点位信息 - 高德地图使用GCJ02坐标系，原始数据就是GCJ02，不需要转换
    const {
      startPointLat,
      startPointLon,
      cusPointLat,
      cusPointLon,
      estimatedDeliveryTime,
      vehicleNumber,
    } = _baseInfo;

    let startLngLat: [number, number] | null = null;
    let endLngLat: [number, number] | null = null;

    if (startPointLat && startPointLon) {
      // 高德地图坐标是 [lng, lat]
      startLngLat = [parseFloat(startPointLat), parseFloat(startPointLon)];
    }
    if (cusPointLat && cusPointLon) {
      endLngLat = [parseFloat(cusPointLat), parseFloat(cusPointLon)];
    }

    if (startLngLat && endLngLat) {
      // 创建起点标记
      const startIcon = new AMap.Icon({
        image: sMarkerPng,
        size: new AMap.Size(40, 40),
        imageSize: new AMap.Size(40, 40),
      });
      const startMarker = new AMap.Marker({
        position: startLngLat,
        icon: startIcon,
        offset: new AMap.Pixel(-20, -40),
      });
      startMarker.setMap(map);
      markersRef.current.push(startMarker);

      // 创建终点标记
      const endIcon = new AMap.Icon({
        image: eMarkerPng,
        size: new AMap.Size(40, 40),
        imageSize: new AMap.Size(40, 40),
      });
      const endMarker = new AMap.Marker({
        position: endLngLat,
        icon: endIcon,
        offset: new AMap.Pixel(-20, -40),
      });
      endMarker.setMap(map);
      markersRef.current.push(endMarker);

      // 创建虚线连接
      const polyline = new AMap.Polyline({
        path: [startLngLat, endLngLat],
        strokeColor: '#3366FF',
        strokeWeight: 2,
        strokeOpacity: 1,
        strokeStyle: 'dashed',
        strokeDasharray: [10, 5],
      });
      polyline.setMap(map);
      polylineRef.current = polyline;

      // 计算中点位置
      const midLng = (startLngLat[0] + endLngLat[0]) / 2;
      const midLat = (startLngLat[1] + endLngLat[1]) / 2;

      // 创建信息窗体内容
      const text = _stateInfo?.label;
      let content = "";
      if (text === "已签收") {
        content = `
          <div class="map-overlay-wrapper" style="width: 70px;">
            <h3><img src='${arrivePng}' style='width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;'/>${text}</h3>
          </div>
        `;
      } else if (text === "待发货") {
        content = `
          <div class="map-overlay-wrapper" style="width: 50px;">
            <h3>${text}</h3>
          </div>
        `;
      } else {
        content = `
          <div class="map-overlay-wrapper" style="width: 230px;">
            <h3>${text}</h3>
            <p>
              ${estimatedDeliveryTime ? `预计送达：${estimatedDeliveryTime}` : ""}
            </p>
            <p>
              ${vehicleNumber ? `配送车辆：${vehicleNumber}` : ""}
            </p>
          </div>
        `;
      }

      // 创建信息窗体
      const infoWindow = new AMap.InfoWindow({
        content,
        // offset: new AMap.Pixel(0, -10),
        offset: new AMap.Pixel(0, 0),
        closeWhenClickMap: false,
        autoMove: false,
      });
      infoWindow.open(map, [midLng, midLat]);
      infoWindowRef.current = infoWindow;

      // 自适应视野
      setTimeout(() => {
        map.setFitView([startMarker, endMarker], false, [80, 80, 80, 80]);
      }, 100);
    }
  }, [delivery, address, mapReady, isActive]);

  return (
    <>
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '400px',
          position: 'relative',
        }}
      />
      <div className="order-info">
        <p className="order-code">
          订单编号：<span>{info?.crmOrderNo}</span>
        </p>
        <p>
          发货时间:<span>{formatDate(info?.nodeTime)}</span>
        </p>
        <p>
          预计送达日期:<span>{delivery?.baseInfo.estimatedDeliveryTime}</span>
        </p>
      </div>
      <Timeline className="collapse-timeline" items={timelineItems} />
    </>
  );
};

const Delivery = ({ crmOrderNo = "", address = "" }) => {
  const [deliveryData, setDeliveryData] = useState<DeliveryDetailItems[]>([]);
  const [deliveryDetails, setDeliveryDetails] = useState<Map<string, DeliveryOrderInfoItem>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewInfo, setPreviewInfo] = useState<{
    id: string;
    matnr: string;
  }>();
  const [activeKey, setActiveKey] = useState<string | string[]>(["0"]);

  const openInspection = useCallback((id: string, items: { matnr: string }[]) => {
    setIsModalOpen(true);
    setPreviewInfo({
      id,
      matnr: items[0].matnr,
    });
  }, []);

  const getDeliveryInfo = useCallback(
    async (crmOrderNo: string) => {
      try {
        const info = await serviceApi.findListByCrmOrderNo2({ crmOrderNo });
        setDeliveryData(info || []);

        // 异步获取每个发货单的详细信息
        info?.forEach((el) => {
          serviceApi
            .getDeliveryOrderInfo({
              deliveryOrderNo: el.orderNo,
              crmOrderNo: el.crmOrderNo,
            })
            .then((res) => {
              const { success, result } = res;
              if (success && result) {
                setDeliveryDetails((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(el.orderNo, result);
                  return newMap;
                });
              }
            })
            .catch((err) => {
              console.log('getDeliveryOrderInfo error:', err);
            });
        });
      } catch (error: any) {
        console.log("error1111", error);
      }
    },
    []
  );

  // 动态生成 Collapse items
  const items: CollapseProps["items"] = useMemo(() => {
    const currentActiveKey = Array.isArray(activeKey) ? activeKey[0] : activeKey;
    
    return deliveryData.map((el, i) => {
      const delivery = deliveryDetails.get(el.orderNo);
      const isActive = currentActiveKey === `${i}`;
      
      return {
        key: `${i}`,
        label: <Collapsetitle info={el} delivery={delivery} openInspection={openInspection} />,
        children: (
          <CollapseBody
            info={el}
            delivery={delivery}
            address={address}
            isActive={isActive}
          />
        ),
      };
    });
  }, [deliveryData, deliveryDetails, activeKey, address, openInspection]);

  useEffect(() => {
    if (crmOrderNo) {
      getDeliveryInfo(crmOrderNo);
    }
  }, [crmOrderNo, getDeliveryInfo]);

  const handleCollapseChange = useCallback((key: string | string[]) => {
    setActiveKey(key);
  }, []);

  return (
    <>
      <Collapse
        className="delivery-wrapper"
        accordion
        items={items}
        size="large"
        activeKey={activeKey}
        onChange={handleCollapseChange}
      />
      <Inspection
        previewInfo={previewInfo}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </>
  );
};

export default Delivery;
